const RATE_LIMIT_CONFIG = {
  LIMITS: {
    SEARCH: { requests: 30, window: 60000, burst: 10 },
    CONTACTS: { requests: 100, window: 60000, burst: 20 },
    COMPANIES: { requests: 50, window: 60000, burst: 15 },
    LOGOS: { requests: 200, window: 60000, burst: 50 },
    METADATA: { requests: 20, window: 60000, burst: 5 },
    DEFAULT: { requests: 60, window: 60000, burst: 15 }
  },
  
  CIRCUIT_BREAKER: {
    FAILURE_THRESHOLD: 5,
    SUCCESS_THRESHOLD: 3,
    TIMEOUT: 30000,
    MONITOR_WINDOW: 60000
  },
  
  QUEUE: {
    MAX_SIZE: 1000,
    MAX_WAIT_TIME: 30000,
    PRIORITY_LEVELS: 5
  }
};

class TokenBucket {
  constructor(capacity, refillRate, refillInterval = 1000) {
    this.capacity = capacity;
    this.tokens = capacity;
    this.refillRate = refillRate;
    this.refillInterval = refillInterval;
    this.lastRefill = Date.now();
    
    this.refillTimer = setInterval(() => this.refill(), this.refillInterval);
  }

  refill() {
    const now = Date.now();
    const timePassed = now - this.lastRefill;
    const tokensToAdd = Math.floor((timePassed / this.refillInterval) * this.refillRate);
    
    this.tokens = Math.min(this.capacity, this.tokens + tokensToAdd);
    this.lastRefill = now;
  }

  consume(tokens = 1) {
    this.refill();
    
    if (this.tokens >= tokens) {
      this.tokens -= tokens;
      return true;
    }
    return false;
  }

  getAvailableTokens() {
    this.refill();
    return this.tokens;
  }

  getTimeUntilRefill() {
    if (this.tokens > 0) return 0;
    
    const timeSinceLastRefill = Date.now() - this.lastRefill;
    const timeUntilNextRefill = this.refillInterval - timeSinceLastRefill;
    return Math.max(0, timeUntilNextRefill);
  }

  destroy() {
    if (this.refillTimer) {
      clearInterval(this.refillTimer);
      this.refillTimer = null;
    }
  }
}

class SlidingWindow {
  constructor(limit, windowSize) {
    this.limit = limit;
    this.windowSize = windowSize;
    this.requests = [];
  }

  isAllowed() {
    const now = Date.now();
    
    this.requests = this.requests.filter(time => now - time < this.windowSize);
    
    if (this.requests.length < this.limit) {
      this.requests.push(now);
      return true;
    }
    
    return false;
  }

  getRequestCount() {
    const now = Date.now();
    this.requests = this.requests.filter(time => now - time < this.windowSize);
    return this.requests.length;
  }

  getTimeUntilReset() {
    if (this.requests.length === 0) return 0;
    
    const oldestRequest = Math.min(...this.requests);
    const timeUntilReset = this.windowSize - (Date.now() - oldestRequest);
    return Math.max(0, timeUntilReset);
  }
}

class CircuitBreaker {
  constructor(config = RATE_LIMIT_CONFIG.CIRCUIT_BREAKER) {
    this.failureThreshold = config.FAILURE_THRESHOLD;
    this.successThreshold = config.SUCCESS_THRESHOLD;
    this.timeout = config.TIMEOUT;
    this.monitorWindow = config.MONITOR_WINDOW;
    
    this.state = 'CLOSED';
    this.failures = 0;
    this.successes = 0;
    this.nextAttempt = 0;
    this.recentEvents = [];
  }

  async execute(request) {
    const now = Date.now();
    
    this.recentEvents = this.recentEvents.filter(
      event => now - event.timestamp < this.monitorWindow
    );

    if (this.state === 'OPEN') {
      if (now < this.nextAttempt) {
        throw new Error('Circuit breaker is OPEN - requests blocked');
      }
      this.state = 'HALF_OPEN';
      this.successes = 0;
    }

    try {
      const result = await request();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure(error);
      throw error;
    }
  }

  onSuccess() {
    this.recentEvents.push({ type: 'success', timestamp: Date.now() });
    
    if (this.state === 'HALF_OPEN') {
      this.successes++;
      if (this.successes >= this.successThreshold) {
        this.state = 'CLOSED';
        this.failures = 0;
        this.successes = 0;
      }
    } else {
      this.failures = 0;
    }
  }

  onFailure(error) {
    this.recentEvents.push({ 
      type: 'failure', 
      timestamp: Date.now(), 
      error: error.message 
    });
    
    this.failures++;
    this.successes = 0;
    
    if (this.failures >= this.failureThreshold) {
      this.state = 'OPEN';
      this.nextAttempt = Date.now() + this.timeout;
    }
  }

  getState() {
    return {
      state: this.state,
      failures: this.failures,
      successes: this.successes,
      nextAttempt: this.nextAttempt,
      recentEvents: this.recentEvents.slice(-10)
    };
  }

  reset() {
    this.state = 'CLOSED';
    this.failures = 0;
    this.successes = 0;
    this.nextAttempt = 0;
    this.recentEvents = [];
  }
}

class RequestQueue {
  constructor(config = RATE_LIMIT_CONFIG.QUEUE) {
    this.maxSize = config.MAX_SIZE;
    this.maxWaitTime = config.MAX_WAIT_TIME;
    this.priorities = Array.from({ length: config.PRIORITY_LEVELS }, () => []);
    this.processing = false;
  }

  enqueue(request, priority = 4) {
    if (this.getTotalSize() >= this.maxSize) {
      throw new Error('Request queue is full');
    }

    const queuedRequest = {
      request,
      priority,
      timestamp: Date.now(),
      resolve: null,
      reject: null
    };

    return new Promise((resolve, reject) => {
      queuedRequest.resolve = resolve;
      queuedRequest.reject = reject;
      
      this.priorities[priority].push(queuedRequest);
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.processing) return;
    this.processing = true;

    try {
      while (this.hasRequests()) {
        const request = this.dequeue();
        if (!request) break;

        if (Date.now() - request.timestamp > this.maxWaitTime) {
          request.reject(new Error('Request timeout in queue'));
          continue;
        }

        try {
          const result = await request.request();
          request.resolve(result);
        } catch (error) {
          request.reject(error);
        }

        await new Promise(resolve => setTimeout(resolve, 10));
      }
    } finally {
      this.processing = false;
    }
  }

  dequeue() {
    // Process by priority (0 = highest priority)
    for (let i = 0; i < this.priorities.length; i++) {
      if (this.priorities[i].length > 0) {
        return this.priorities[i].shift();
      }
    }
    return null;
  }

  hasRequests() {
    return this.priorities.some(queue => queue.length > 0);
  }

  getTotalSize() {
    return this.priorities.reduce((total, queue) => total + queue.length, 0);
  }

  clear() {
    this.priorities.forEach(queue => {
      queue.forEach(request => {
        request.reject(new Error('Queue cleared'));
      });
      queue.length = 0;
    });
  }

  getStats() {
    return {
      totalQueued: this.getTotalSize(),
      byPriority: this.priorities.map(queue => queue.length),
      processing: this.processing
    };
  }
}

class RateLimiter {
  constructor() {
    this.limiters = new Map();
    this.circuitBreakers = new Map();
    this.queue = new RequestQueue();
    this.stats = {
      totalRequests: 0,
      allowedRequests: 0,
      deniedRequests: 0,
      queuedRequests: 0,
      errors: 0
    };
    
    this.pendingRequests = new Map();
    
    this.adaptiveConfig = {
      enabled: true,
      adjustmentFactor: 0.1,
      minAdjustment: 0.5,
      maxAdjustment: 2.0
    };
    
    this.initializeLimiters();
    this.startPeriodicCleanup();
  }

  initializeLimiters() {
    Object.entries(RATE_LIMIT_CONFIG.LIMITS).forEach(([endpoint, config]) => {
      this.limiters.set(endpoint, {
        tokenBucket: new TokenBucket(config.burst, config.requests / (config.window / 60000)),
        slidingWindow: new SlidingWindow(config.requests, config.window),
        config: { ...config }
      });
    });
  }

  startPeriodicCleanup() {
    setInterval(() => {
      const now = Date.now();
      for (const [key, request] of this.pendingRequests.entries()) {
        if (now - request.timestamp > 60000) {
          this.pendingRequests.delete(key);
        }
      }
    }, 60000);
  }

  getEndpointType(url) {
    if (url.includes('/search')) return 'SEARCH';
    if (url.includes('/contacts')) return 'CONTACTS';
    if (url.includes('/companies')) return 'COMPANIES';
    if (url.includes('/logos')) return 'LOGOS';
    if (url.includes('/metadata')) return 'METADATA';
    return 'DEFAULT';
  }

  generateRequestKey(url, options = {}) {
    const method = options.method || 'GET';
    const body = options.body ? JSON.stringify(options.body) : '';
    return `${method}:${url}:${body}`;
  }

  async executeRequest(url, options = {}, priority = 4) {
    const requestKey = this.generateRequestKey(url, options);
    const endpointType = this.getEndpointType(url);
    
    this.stats.totalRequests++;

    if (this.pendingRequests.has(requestKey)) {
      return this.pendingRequests.get(requestKey).promise;
    }

    if (!this.circuitBreakers.has(endpointType)) {
      this.circuitBreakers.set(endpointType, new CircuitBreaker());
    }
    const circuitBreaker = this.circuitBreakers.get(endpointType);

    const executeActualRequest = async () => {
      return circuitBreaker.execute(async () => {
        const response = await fetch(url, options);
        
        this.handleRateLimitHeaders(response, endpointType);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        return response;
      });
    };

    const limiter = this.limiters.get(endpointType) || this.limiters.get('DEFAULT');
    
    if (!limiter.tokenBucket.consume() || !limiter.slidingWindow.isAllowed()) {
      this.stats.deniedRequests++;
      
      this.stats.queuedRequests++;
      const pendingRequest = {
        promise: this.queue.enqueue(executeActualRequest, priority),
        timestamp: Date.now()
      };
      
      this.pendingRequests.set(requestKey, pendingRequest);
      
      try {
        const result = await pendingRequest.promise;
        this.stats.allowedRequests++;
        return result;
      } catch (error) {
        this.stats.errors++;
        throw error;
      } finally {
        this.pendingRequests.delete(requestKey);
      }
    }

    const pendingRequest = {
      promise: executeActualRequest(),
      timestamp: Date.now()
    };
    
    this.pendingRequests.set(requestKey, pendingRequest);
    
    try {
      const result = await pendingRequest.promise;
      this.stats.allowedRequests++;
      return result;
    } catch (error) {
      this.stats.errors++;
      throw error;
    } finally {
      this.pendingRequests.delete(requestKey);
    }
  }

  handleRateLimitHeaders(response, endpointType) {
    const remaining = response.headers.get('X-RateLimit-Remaining');
    const limit = response.headers.get('X-RateLimit-Limit');

    if (remaining !== null && limit !== null && this.adaptiveConfig.enabled) {
      const remainingRatio = parseInt(remaining) / parseInt(limit);
      const limiter = this.limiters.get(endpointType);
      
      if (limiter && remainingRatio < 0.2) {
        const adjustment = Math.max(
          this.adaptiveConfig.minAdjustment,
          1 - this.adaptiveConfig.adjustmentFactor
        );
        
        limiter.config.requests = Math.floor(limiter.config.requests * adjustment);
        console.log(`Adaptive rate limiting: reduced ${endpointType} to ${limiter.config.requests} req/min`);
      }
    }

    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After');
      if (retryAfter) {
        const delay = parseInt(retryAfter) * 1000;
        console.log(`Rate limited by server, backing off for ${delay}ms`);
        
        const limiter = this.limiters.get(endpointType);
        if (limiter) {
          limiter.config.requests = Math.floor(limiter.config.requests * 0.5);
        }
      }
    }
  }

  async throttle(endpointType = 'DEFAULT') {
    if (!this.limiters.has(endpointType)) {
      const config = RATE_LIMIT_CONFIG.LIMITS[endpointType] || RATE_LIMIT_CONFIG.LIMITS.DEFAULT;
      this.limiters.set(endpointType, {
        tokenBucket: new TokenBucket(config.burst, config.requests / (config.window / 60000)),
        slidingWindow: new SlidingWindow(config.requests, config.window),
        config: { ...config }
      });
    }

    const { slidingWindow } = this.limiters.get(endpointType);
    while (!slidingWindow.isAllowed()) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  async get(url, options = {}) {
    return this.executeRequest(url, { ...options, method: 'GET' }, 2);
  }

  async post(url, data, options = {}) {
    return this.executeRequest(url, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    }, 1);
  }

  async put(url, data, options = {}) {
    return this.executeRequest(url, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    }, 1);
  }

  async delete(url, options = {}) {
    return this.executeRequest(url, { ...options, method: 'DELETE' }, 0);
  }

  async batchRequests(requests, maxConcurrency = 5) {
    const results = [];
    const batches = [];
    
    for (let i = 0; i < requests.length; i += maxConcurrency) {
      batches.push(requests.slice(i, i + maxConcurrency));
    }
    
    for (const batch of batches) {
      const batchPromises = batch.map(({ url, options, priority }) =>
        this.executeRequest(url, options, priority).catch(error => ({ error }))
      );
      
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
      
      if (batches.indexOf(batch) < batches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    return results;
  }

  getStats() {
    const limiterStats = {};
    for (const [endpoint, limiter] of this.limiters.entries()) {
      limiterStats[endpoint] = {
        availableTokens: limiter.tokenBucket.getAvailableTokens(),
        capacity: limiter.tokenBucket.capacity,
        requestCount: limiter.slidingWindow.getRequestCount(),
        limit: limiter.config.requests,
        timeUntilReset: limiter.slidingWindow.getTimeUntilReset()
      };
    }

    const circuitBreakerStats = {};
    for (const [endpoint, breaker] of this.circuitBreakers.entries()) {
      circuitBreakerStats[endpoint] = breaker.getState();
    }

    return {
      global: { ...this.stats },
      limiters: limiterStats,
      circuitBreakers: circuitBreakerStats,
      queue: this.queue.getStats(),
      pendingRequests: this.pendingRequests.size
    };
  }

  reset() {
    this.stats = {
      totalRequests: 0,
      allowedRequests: 0,
      deniedRequests: 0,
      queuedRequests: 0,
      errors: 0
    };
    
    this.limiters.clear();
    this.circuitBreakers.clear();
    this.queue.clear();
    this.pendingRequests.clear();
    
    this.initializeLimiters();
  }

  updateLimits(endpoint, newLimits) {
    const limiter = this.limiters.get(endpoint);
    if (limiter) {
      limiter.config = { ...limiter.config, ...newLimits };
      
      limiter.tokenBucket.destroy();
      limiter.tokenBucket = new TokenBucket(
        newLimits.burst || limiter.config.burst,
        (newLimits.requests || limiter.config.requests) / (limiter.config.window / 60000)
      );
      
      limiter.slidingWindow = new SlidingWindow(
        newLimits.requests || limiter.config.requests,
        newLimits.window || limiter.config.window
      );
    }
  }

  destroy() {
    this.limiters.forEach(limiter => {
      limiter.tokenBucket.destroy();
    });
    this.limiters.clear();
    this.circuitBreakers.clear();
    this.queue.clear();
    this.pendingRequests.clear();
  }
}

const rateLimiter = new RateLimiter();

export default rateLimiter;

export {
  RateLimiter,
  TokenBucket,
  SlidingWindow,
  CircuitBreaker,
  RequestQueue,
  RATE_LIMIT_CONFIG
};

export const rateLimitedFetch = {
  get: (url, options) => rateLimiter.get(url, options),
  post: (url, data, options) => rateLimiter.post(url, data, options),
  put: (url, data, options) => rateLimiter.put(url, data, options),
  delete: (url, options) => rateLimiter.delete(url, options),
  batch: (requests, maxConcurrency) => rateLimiter.batchRequests(requests, maxConcurrency),
  stats: () => rateLimiter.getStats(),
  reset: () => rateLimiter.reset()
};
